import React from 'react';
import { useSearchParams } from 'react-router-dom';
import SEO from '../components/SEO';
import { COMPANY, LAST_UPDATED, LAST_UPDATED_EN } from '../data/company';
import './LegalPage.css';

const RefundPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const lang = searchParams.get('lang') === 'en' ? 'en' : 'tr';

  const setLang = (next) => {
    const params = new URLSearchParams(searchParams);
    if (next === 'tr') params.delete('lang');
    else params.set('lang', next);
    setSearchParams(params, { replace: true });
  };

  const distanceSalesHref = lang === 'en' ? '/mesafeli-satis-sozlesmesi?lang=en' : '/mesafeli-satis-sozlesmesi';

  return (
    <div className="legal-page">
      <SEO
        title={lang === 'en' ? 'Delivery and Refund Terms' : 'Teslimat ve İade Şartları'}
        path="/iptal-iade"
      />
      <div className="legal-page__container">
        <div className="legal-page__toolbar">
          <div className="legal-page__lang" role="group" aria-label="Language">
            <button type="button" aria-pressed={lang === 'tr'} onClick={() => setLang('tr')}>TR</button>
            <button type="button" aria-pressed={lang === 'en'} onClick={() => setLang('en')}>EN</button>
          </div>
        </div>

        {lang === 'en' ? (
          <>
            <h1>Delivery and Refund Terms</h1>
            <p className="legal-page__date">Last updated: {LAST_UPDATED_EN}</p>

            <p>
              Subscriptions purchased through {COMPANY.brand} are digital services that do not
              require physical delivery. The service is activated as soon as payment is confirmed.
            </p>

            <h2>1. Delivery</h2>
            <ul>
              <li>Because the service sold is a <strong>digital subscription</strong>, there is no physical delivery, shipping, or delivery fee.</li>
              <li>As soon as payment is confirmed, the subscription is <strong>activated automatically</strong>; access to the features included in the plan begins immediately.</li>
              <li>Delivery is considered complete once the service is provisioned to your account. This typically takes <strong>only a few seconds</strong>.</li>
              <li>If payment was received but the subscription was not activated, contact {COMPANY.email}; the issue will be resolved or the amount will be refunded.</li>
              <li>An internet connection and an up-to-date web browser are sufficient to use the service.</li>
            </ul>

            <h2>2. Cancelling Your Subscription</h2>
            <ul>
              <li>You may cancel your subscription <strong>at any time</strong>, without giving a reason.</li>
              <li>Cancellation can be done via the <strong>Cancel Subscription</strong> button on the <strong>Panel → My Subscription</strong> screen, or by emailing {COMPANY.email}.</li>
              <li>Cancellation takes effect at the end of your current billing period. You keep access to the service until the end of that period.</li>
              <li>A cancelled subscription is <strong>not billed</strong> for the next period; no further charge is made to your card.</li>
              <li>No partial refund is given for the remaining days when cancelling mid-period.</li>
            </ul>

            <h2>3. Right of Withdrawal</h2>
            <p>
              Under Article 15(ğ) of the Turkish Distance Contracts Regulation, the right of
              withdrawal <strong>cannot be exercised</strong> for contracts relating to services
              performed immediately in electronic form, or intangible goods delivered instantly to
              the consumer. The service is deemed performed as soon as the subscription is purchased.
            </p>

            <h2>4. Refund Conditions</h2>
            <p>
              While there is no legal obligation to do so, refund requests are evaluated, as a
              matter of customer satisfaction, in the following cases:
            </p>
            <ul>
              <li><strong>Unused subscription:</strong> a full refund is issued if requested within 14 days of the payment date and none of the plan's features (automated form draft preparation, job fit analysis, etc.) have been used.</li>
              <li><strong>Technical failure:</strong> if the service could not be provided due to a fault on our side, the fee for the unusable period is refunded.</li>
              <li><strong>Duplicate charge:</strong> if more than one payment was taken for the same subscription, the excess charge is refunded unconditionally.</li>
            </ul>
            <p>
              Refund requests made after the plan's features have started being used cannot be
              honored, as the service is considered performed.
            </p>

            <h2>5. Refund Process</h2>
            <ul>
              <li>Send your refund request to <a href={`mailto:${COMPANY.email}`}>{COMPANY.email}</a> from the email address registered to your account.</li>
              <li>Requests are reviewed within <strong>3 business days</strong> at the latest, and you will be notified of the outcome.</li>
              <li>Approved refunds are issued to the original payment card, in the same currency, within <strong>14 days</strong> of approval.</li>
              <li>The time for the amount to appear in your card account depends on your bank; it typically takes 2–10 business days. {COMPANY.brand} is not responsible for this duration.</li>
            </ul>

            <h2>6. Free Plan</h2>
            <p>
              No payment is taken for the free plan, so no cancellation or refund process applies.
              When a paid subscription is cancelled, your account reverts to the free plan and your
              data is not deleted.
            </p>

            <h2>7. Payment Security</h2>
            <p>
              Payments are processed through the licensed payment institution <strong>iyzico</strong>'s
              infrastructure with <strong>3D Secure</strong> verification. Your card details are not
              stored on {COMPANY.brand}'s servers and are not shared with third parties.
            </p>

            <h2>8. Contact</h2>
            <ul>
              <li><strong>Legal name:</strong> {COMPANY.legalName}</li>
              <li><strong>Address:</strong> {COMPANY.address}</li>
              <li><strong>Phone:</strong> {COMPANY.phone}</li>
              <li><strong>Email:</strong> <a href={`mailto:${COMPANY.email}`}>{COMPANY.email}</a></li>
            </ul>

            <p>
              For detailed terms, see the <a href={distanceSalesHref}>Distance Sales Agreement</a> page.
            </p>
          </>
        ) : (
          <>
            <h1>Teslimat ve İade Şartları</h1>
            <p className="legal-page__date">Son güncelleme: {LAST_UPDATED}</p>

            <p>
              {COMPANY.brand} üzerinden satın alınan abonelikler, fiziksel teslimat gerektirmeyen
              dijital hizmetlerdir. Ödeme onaylandığı anda hizmet aktif hâle gelir.
            </p>

            <h2>1. Teslimat</h2>
            <ul>
              <li>Satışa konu hizmet <strong>dijital abonelik</strong> olduğundan fiziksel teslimat, kargo veya teslimat ücreti bulunmamaktadır.</li>
              <li>Ödeme onaylandığı anda abonelik <strong>otomatik olarak aktif edilir</strong>; paket kapsamındaki özelliklere aynı anda erişim başlar.</li>
              <li>Teslimat, hizmetin hesabınıza tanımlanmasıyla gerçekleşmiş sayılır. Ortalama tamamlanma süresi <strong>birkaç saniyedir</strong>.</li>
              <li>Ödeme alındığı hâlde abonelik aktif olmazsa {COMPANY.email} adresine yazın; sorun giderilir veya bedel iade edilir.</li>
              <li>Hizmetten yararlanabilmek için internet bağlantısı ve güncel bir web tarayıcısı yeterlidir.</li>
            </ul>

            <h2>2. Abonelik İptali</h2>
            <ul>
              <li>Aboneliğinizi <strong>dilediğiniz zaman</strong>, herhangi bir gerekçe göstermeden iptal edebilirsiniz.</li>
              <li>İptal işlemi <strong>Panel → Aboneliğim</strong> ekranındaki <strong>Aboneliği İptal Et</strong> butonuyla veya {COMPANY.email} adresine e-posta göndererek yapılabilir.</li>
              <li>İptal, içinde bulunduğunuz ödeme döneminin sonunda geçerli olur. Dönem sonuna kadar hizmetten yararlanmaya devam edersiniz.</li>
              <li>İptal edilen abonelik bir sonraki dönemde <strong>ücretlendirilmez</strong>; kartınızdan yeni çekim yapılmaz.</li>
              <li>Dönem ortasında yapılan iptallerde kalan günler için kısmi ücret iadesi yapılmaz.</li>
            </ul>

            <h2>3. Cayma Hakkı</h2>
            <p>
              Mesafeli Sözleşmeler Yönetmeliği'nin 15. maddesinin (ğ) bendi uyarınca, elektronik
              ortamda anında ifa edilen hizmetler ile tüketiciye anında teslim edilen gayrimaddi
              mallara ilişkin sözleşmelerde <strong>cayma hakkı kullanılamaz</strong>. Abonelik
              satın alındığı anda hizmet ifa edilmiş sayılır.
            </p>

            <h2>4. İade Koşulları</h2>
            <p>
              Yasal zorunluluk bulunmamakla birlikte, müşteri memnuniyeti gereği aşağıdaki
              durumlarda iade talepleri değerlendirilir:
            </p>
            <ul>
              <li><strong>Kullanılmamış abonelik:</strong> Ödeme tarihinden itibaren 14 gün içinde başvurulması ve paket kapsamındaki hiçbir özelliğin (form taslağı hazırlama, ilan uyum analizi vb.) kullanılmamış olması hâlinde bedelin tamamı iade edilir.</li>
              <li><strong>Teknik arıza:</strong> Hizmetin bizden kaynaklanan bir arıza nedeniyle sunulamaması hâlinde, kullanılamayan döneme ilişkin bedel iade edilir.</li>
              <li><strong>Mükerrer çekim:</strong> Aynı abonelik için birden fazla tahsilat yapılmışsa fazla tahsilat koşulsuz iade edilir.</li>
            </ul>
            <p>
              Paket kapsamındaki özellikler kullanılmaya başlandıktan sonra yapılan iade talepleri,
              hizmet ifa edilmiş sayıldığından karşılanamaz.
            </p>

            <h2>5. İade Süreci</h2>
            <ul>
              <li>İade talebinizi <a href={`mailto:${COMPANY.email}`}>{COMPANY.email}</a> adresine, hesabınıza kayıtlı e-posta adresinden iletin.</li>
              <li>Talepler en geç <strong>3 iş günü</strong> içinde değerlendirilir ve sonucu size bildirilir.</li>
              <li>Onaylanan iadeler, ödemenin yapıldığı karta ve aynı para birimiyle, onay tarihinden itibaren <strong>14 gün</strong> içinde iade edilir.</li>
              <li>Tutarın kart hesabınıza yansıma süresi bankanıza bağlıdır; genellikle 2–10 iş günü sürer. Bu süreden {COMPANY.brand} sorumlu değildir.</li>
            </ul>

            <h2>6. Ücretsiz Paket</h2>
            <p>
              Ücretsiz pakette herhangi bir ödeme alınmadığından iptal veya iade süreci
              işletilmez. Ücretli abonelik iptal edildiğinde hesabınız ücretsiz pakete döner ve
              verileriniz silinmez.
            </p>

            <h2>7. Ödeme Güvenliği</h2>
            <p>
              Ödemeler, lisanslı ödeme kuruluşu <strong>iyzico</strong> altyapısı üzerinden{' '}
              <strong>3D Secure</strong> doğrulaması ile alınır. Kart bilgileriniz {COMPANY.brand} sunucularında saklanmaz ve
              üçüncü kişilerle paylaşılmaz.
            </p>

            <h2>8. İletişim</h2>
            <ul>
              <li><strong>Ünvan:</strong> {COMPANY.legalName}</li>
              <li><strong>Adres:</strong> {COMPANY.address}</li>
              <li><strong>Telefon:</strong> {COMPANY.phone}</li>
              <li><strong>E-posta:</strong> <a href={`mailto:${COMPANY.email}`}>{COMPANY.email}</a></li>
            </ul>

            <p>
              Ayrıntılı hükümler için <a href={distanceSalesHref}>Mesafeli Satış Sözleşmesi</a>{' '}
              sayfasına bakabilirsiniz.
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default RefundPage;
